import {
  DEFAULT_THEME_PALETTE_ID,
  LEGACY_WEB_THEME_TO_PALETTE,
  THEME_PALETTE_IDS,
  normalizeThemePaletteId,
  type ThemeMode,
  type ThemeTokenSet,
  type ThemePaletteId,
} from "./palettes";
import {
  normalizeSyncedThemeUnlockIds,
  resolveStoredPaletteSelection,
} from "./palette-selection.mjs";
import {
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";

export const THEME_CHANGED_EVENT = "tb-theme-changed";

const LEGACY_MODE_STORAGE_KEY = "tb_web_theme_v1";
const MODE_STORAGE_KEY = "tb_web_theme_mode_v2";
const PALETTE_STORAGE_KEY = "tb_web_theme_palette_v2";
const AUTO_TRANSITION_STORAGE_KEY = "tb_web_theme_auto_transition_v1";
const HIGH_CONTRAST_STORAGE_KEY = "tb_web_theme_high_contrast_v1";
const UNLOCKED_THEME_IDS_STORAGE_KEY = "tb_web_theme_unlocked_ids_v1";
const THEME_BOOTSTRAP_STORAGE_KEY = "tb_web_theme_bootstrap_v1";

export type ThemeSyncPayload = {
  uiTheme: ThemeMode;
  appThemePalette: string;
  themeAutoTransitionEnabled: boolean;
  themeAuto: boolean;
  highContrast: boolean;
};

export function writeThemeBootstrapSnapshot({
  mode,
  resolved,
  paletteId,
  highContrast,
  manualHighContrast,
  tokens,
}: {
  mode: ThemeMode;
  resolved: "light" | "dark";
  paletteId: ThemePaletteId;
  highContrast: boolean;
  manualHighContrast: boolean;
  tokens: ThemeTokenSet;
}) {
  try {
    // The blocking bootstrap reads only the current account's last-painted
    // presentation snapshot. Keep it behind the same account boundary as theme state.
    writeAccountScopedStorage(
      THEME_BOOTSTRAP_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        mode,
        resolved,
        paletteId,
        highContrast,
        manualHighContrast,
        themeColor: tokens.metaThemeColor,
        cssVars: tokens.cssVars,
      }),
    );
  } catch {
    /* The applied in-memory theme remains authoritative when storage is unavailable. */
  }
}

export function readStoredThemeMode(): ThemeMode {
  try {
    const s = (readAccountScopedStorage(MODE_STORAGE_KEY) ||
      localStorage.getItem(LEGACY_MODE_STORAGE_KEY)) as ThemeMode | null;
    return normalizeThemeMode(s);
  } catch {
    return "system";
  }
}

export function writeStoredThemeMode(mode: ThemeMode) {
  writeAccountScopedStorage(MODE_STORAGE_KEY, normalizeThemeMode(mode));
  dispatchThemeChanged();
}

export function readStoredThemePaletteId(): ThemePaletteId {
  return readStoredThemePaletteSelection().renderId;
}

export function readStoredThemePaletteSourceId(): string {
  return readStoredThemePaletteSelection().storedId;
}

export function readStoredThemePaletteSelection(): {
  storedId: string;
  renderId: ThemePaletteId;
  supported: boolean;
} {
  try {
    return resolveStoredPaletteSelection(
      readAccountScopedStorage(PALETTE_STORAGE_KEY) ||
        localStorage.getItem("tb_web_palette_v1") ||
        localStorage.getItem("tb-theme") ||
        DEFAULT_THEME_PALETTE_ID,
      THEME_PALETTE_IDS,
      DEFAULT_THEME_PALETTE_ID,
      LEGACY_WEB_THEME_TO_PALETTE,
    );
  } catch {
    return {
      storedId: DEFAULT_THEME_PALETTE_ID,
      renderId: DEFAULT_THEME_PALETTE_ID,
      supported: true,
    };
  }
}

export function writeStoredThemePaletteId(id: ThemePaletteId) {
  writeAccountScopedStorage(PALETTE_STORAGE_KEY, normalizeThemePaletteId(id));
  dispatchThemeChanged();
}

export function readThemeSyncPayload(): ThemeSyncPayload {
  const mode = readStoredThemeMode();
  const themeAutoTransitionEnabled = readStoredBoolean(AUTO_TRANSITION_STORAGE_KEY, false);
  const highContrast = readStoredHighContrast();
  return {
    uiTheme: mode,
    appThemePalette: readStoredThemePaletteSourceId(),
    themeAutoTransitionEnabled,
    themeAuto: themeAutoTransitionEnabled,
    highContrast,
  };
}

export function applyThemeSyncPayload(theme: object | null | undefined): boolean {
  if (!theme || typeof theme !== "object") return false;
  const data = theme as Record<string, unknown>;
  const owns = (key: string) => Object.prototype.hasOwnProperty.call(data, key);
  const mode = owns("uiTheme")
    ? data.uiTheme == null
      ? "system"
      : normalizeThemeMode(data.uiTheme)
    : readStoredThemeMode();
  const paletteKey = ["appThemePalette", "paletteId", "preset"].find(owns);
  const rawPaletteId = paletteKey ? data[paletteKey] : undefined;
  const paletteSelection = !paletteKey
    ? readStoredThemePaletteSelection()
    : resolveStoredPaletteSelection(
        rawPaletteId == null ? DEFAULT_THEME_PALETTE_ID : rawPaletteId,
        THEME_PALETTE_IDS,
        DEFAULT_THEME_PALETTE_ID,
        LEGACY_WEB_THEME_TO_PALETTE,
      );
  const hasAutoTransition =
    Object.prototype.hasOwnProperty.call(data, "themeAutoTransitionEnabled") ||
    Object.prototype.hasOwnProperty.call(data, "themeAuto");
  const themeAutoTransitionEnabled = hasAutoTransition
    ? data.themeAutoTransitionEnabled == null && data.themeAuto == null
      ? false
      : Boolean(data.themeAutoTransitionEnabled ?? data.themeAuto)
    : readStoredBoolean(AUTO_TRANSITION_STORAGE_KEY, false);
  const highContrast = owns("highContrast")
    ? data.highContrast == null
      ? false
      : Boolean(data.highContrast)
    : readStoredHighContrast();
  const unlockedThemeIds = normalizeSyncedThemeUnlockIds(data.unlockedThemeIds);
  try {
    writeAccountScopedStorage(MODE_STORAGE_KEY, mode);
    writeAccountScopedStorage(PALETTE_STORAGE_KEY, paletteSelection.storedId);
    writeAccountScopedStorage(AUTO_TRANSITION_STORAGE_KEY, String(themeAutoTransitionEnabled));
    writeAccountScopedStorage(HIGH_CONTRAST_STORAGE_KEY, String(highContrast));
    if (unlockedThemeIds) {
      writeAccountScopedStorage(UNLOCKED_THEME_IDS_STORAGE_KEY, JSON.stringify(unlockedThemeIds));
    }
    dispatchThemeChanged();
    return true;
  } catch {
    return false;
  }
}

export function readStoredHighContrast(): boolean {
  return readStoredBoolean(HIGH_CONTRAST_STORAGE_KEY, false);
}

export function writeStoredHighContrast(enabled: boolean) {
  writeAccountScopedStorage(HIGH_CONTRAST_STORAGE_KEY, String(enabled));
  dispatchThemeChanged();
}

export function readStoredUnlockedThemeIds(): readonly string[] {
  try {
    const raw = readAccountScopedStorage(UNLOCKED_THEME_IDS_STORAGE_KEY);
    return normalizeSyncedThemeUnlockIds(raw ? JSON.parse(raw) : []) || [];
  } catch {
    return [];
  }
}

function normalizeThemeMode(value: unknown): ThemeMode {
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

function readStoredBoolean(key: string, fallback: boolean): boolean {
  try {
    const raw = readAccountScopedStorage(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function dispatchThemeChanged() {
  window.dispatchEvent(new Event(THEME_CHANGED_EVENT));
}
